/**
 * Theme utility functions
 * Converts platform theme configuration to PrimeNG theme format
 */

import { ThemeConfig } from '@platform/config';
import type { PresetOptions } from './theme.types';

/**
 * Converts a hex color to RGB values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Converts RGB to hex
 */
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

/**
 * Generates a color scale (50-950) from a base color
 * Creates lighter and darker shades for the color palette
 */
function generateColorScale(baseColor: string): Record<string, string> {
  const rgb = hexToRgb(baseColor);
  if (!rgb) return {};

  const scale: Record<string, string> = {};
  const steps = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

  steps.forEach((step) => {
    const factor = (500 - step) / 500; // -0.9 to 0.9
    let r = Math.round(rgb.r + (255 - rgb.r) * factor * 0.9);
    let g = Math.round(rgb.g + (255 - rgb.g) * factor * 0.9);
    let b = Math.round(rgb.b + (255 - rgb.b) * factor * 0.9);

    // Clamp values
    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));

    scale[step] = rgbToHex(r, g, b);
  });

  // Ensure 500 is the base color
  scale[500] = baseColor;

  return scale;
}

/**
 * Converts platform theme configuration to PrimeNG preset options
 */
export function convertThemeConfigToPreset(
  themeConfig: ThemeConfig,
  brandingConfig?: { primaryColor?: string; secondaryColor?: string; accentColor?: string }
): Partial<PresetOptions> {
  const preset: Partial<PresetOptions> = {
    semantic: {},
  };

  // Use branding colors as fallback if theme colors are not provided
  const primaryColor = themeConfig.themes?.light?.primary || brandingConfig?.primaryColor || '#3b82f6';
  const secondaryColor = themeConfig.themes?.light?.secondary || brandingConfig?.secondaryColor || '#8b5cf6';
  const accentColor = themeConfig.themes?.light?.accent || brandingConfig?.accentColor || '#10b981';

  // Generate color scales
  if (primaryColor) {
    preset.semantic = {
      ...preset.semantic,
      primary: generateColorScale(primaryColor),
    };
  }

  if (secondaryColor) {
    preset.semantic = {
      ...preset.semantic,
      secondary: generateColorScale(secondaryColor),
    };
  }

  if (accentColor) {
    preset.semantic = {
      ...preset.semantic,
      accent: generateColorScale(accentColor),
    };
  }

  // Map surface colors if provided
  if (themeConfig.themes?.light?.surface || themeConfig.themes?.dark?.surface) {
    preset.semantic = {
      ...preset.semantic,
      colorScheme: {
        light: themeConfig.themes?.light?.surface
          ? {
              surface: {
                0: themeConfig.themes.light.background || '#ffffff',
                50: themeConfig.themes.light.surface || '#f9fafb',
                // Add more surface shades if needed
              },
            }
          : undefined,
        dark: themeConfig.themes?.dark?.surface
          ? {
              surface: {
                0: themeConfig.themes.dark.background || '#111827',
                50: themeConfig.themes.dark.surface || '#1f2937',
                // Add more surface shades if needed
              },
            }
          : undefined,
      },
    };
  }

  return preset;
}

